import React, { Fragment, useEffect } from 'react';
import { HashRouter as Router, Route, Navigate, Routes } from 'react-router-dom';
import Header from '../Header/Header';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import Trainer from '../Trainer/Trainer';
import Settings from '../Settings/Settings';
import UserName from '../UserName/UserName';
import InactiveTrainer from '../InactiveTrainer/InactiveTrainer';
import LowCallVolumeReminder from '../../components/LowCallVolumeReminder/LowCallVolumeReminder';
import Login from '../Login/Login';
import CallSimulator from '../CallSimulator/CallSimulator';
import { TRAINER_PAGE, SETTINGS_PAGE, LOGIN_PAGE, CALL_SIMULATOR_PAGE } from '../../utils/routes';
import { userReload } from '../../store/actions/trainer';
import { NoTeamCard } from '../../components/NoTeamCard/NoTeamCard';
import useLocalization from '../../localization/useLocalization';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getAuthToken, isAuthWithPasswordEnabled } from '../../store/selectors/auth';
import type { RootState } from '../../store/types';
import { clearStartupLoadingTimeout, markCoachLoaded } from '../../store/startup';

function App() {
    const dispatch = useAppDispatch();
    const localization = useLocalization();

    const { errorCode, countdown, requireOnboarding, isTrainerActive, isAuthenticated, lowCallVolumeReminderVisible } = useAppSelector(
        (store: RootState) => ({
            errorCode: store.trainer.errorCode,
            countdown: store.trainer.countdown,
            requireOnboarding:
                store.trainer.userInterfaceConfig.requirePersonalUsername && store.user.initialized && !store.user.personalUsername,
            isTrainerActive: store.trainer.isTrainerActive,
            isAuthenticated: !!getAuthToken(store) || !isAuthWithPasswordEnabled(store),
            lowCallVolumeReminderVisible: store.lowCallVolumeReminder.alertVisible
        })
    );

    useEffect(() => {
        clearStartupLoadingTimeout();
        markCoachLoaded();
    }, []);

    const renderErrorMessage = () => {
        if (!errorCode) return null;

        return <ErrorMessage localization={localization} errorCode={errorCode} countdown={countdown} />;
    };

    const renderRoutes = () => (
        <Fragment>
            <Header showMenu={isAuthenticated} />
            {renderErrorMessage()}
            <Routes>
                <Route key="HomeRoute" path="/" element={<Navigate to={TRAINER_PAGE} replace />} />
                <Route
                    key="TrainerRoute"
                    path={TRAINER_PAGE}
                    element={
                        !isAuthenticated ? (
                            <Navigate to={LOGIN_PAGE} replace />
                        ) : (
                            <Fragment>
                                <div style={{ marginTop: 40 }} />
                                <NoTeamCard reload={() => dispatch(userReload())} />
                                <Trainer />
                            </Fragment>
                        )
                    }
                />
                <Route
                    key="SettingsRoute"
                    path={SETTINGS_PAGE}
                    element={!isAuthenticated ? <Navigate to={LOGIN_PAGE} replace /> : <Settings />}
                />
                <Route
                    key="CallSimulatorRoute"
                    path={CALL_SIMULATOR_PAGE}
                    element={!isAuthenticated ? <Navigate to={LOGIN_PAGE} replace /> : <CallSimulator />}
                />
                <Route key="LoginRoute" path={LOGIN_PAGE} element={isAuthenticated ? <Navigate to={TRAINER_PAGE} replace /> : <Login />} />
            </Routes>
        </Fragment>
    );

    if (isAuthenticated) {
        if (!isTrainerActive) return <InactiveTrainer />;
        if (requireOnboarding) return <UserName />;
    }

    // The reminder is a full-screen overlay rendered ON TOP of the app rather
    // than replacing it — swapping the whole tree out on a background dispatch
    // unmounts subscribed components and triggers React "state update on
    // unmounted component" warnings.
    return (
        <Fragment>
            <Router>{renderRoutes()}</Router>
            {isAuthenticated && lowCallVolumeReminderVisible && <LowCallVolumeReminder />}
        </Fragment>
    );
}

export default App;
